/**
 * Règles de la page « Mes commandes », portées de l'app mobile
 * (WOSH Mobile V2 : app/(app)/orders.tsx, features/orders/use-live-tracking.ts,
 * features/checkout/slot-utils.ts, features/orders/order-place.ts).
 *
 * Tout est pur : `t` est la fonction i18next de l'espace « app » (les textes
 * du mobile, mêmes clés), `lang` vaut "fr" | "en" | "nl" et `now` peut être
 * fixé par les tests. Aucun appel réseau ici.
 */

/* ── Statut ──────────────────────────────────────────────────────────────── */

/**
 * États du serveur → six statuts d'affichage. Porté tel quel, y compris la
 * bizarrerie connue : `picked_up` tombe sur `driver_pickup`, dont la frise
 * garde l'étape collecte active ; elle ne passe « faite » qu'à `dropped_*`.
 * À corriger sur les deux plateformes en même temps, pas sur le web seul.
 */
export function mapUiStatus(status) {
  if (!status) return "to_confirm";
  const s = String(status).toLowerCase().replace(/[\s-]+/g, "_");

  if (s.includes("arriv") || s.includes("near")) return "arriving";
  if (
    s === "out_for_delivery" ||
    s.includes("out_for") ||
    s === "delivery" ||
    s.includes("retrieved")
  )
    return "out_for_delivery";
  if (s.includes("ready") || s.includes("fold")) return "ready";
  if (
    s.includes("dropped") ||
    s.includes("at_laundry") ||
    s.includes("washed") ||
    s.includes("process")
  )
    return "at_laundry";
  if (
    s.includes("driver") ||
    s.includes("pickup") ||
    s.includes("picked") ||
    s.includes("route")
  )
    return "driver_pickup";

  return "to_confirm";
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

const MONTHS_EN = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

const SHORT_DAYS = {
  fr: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  nl: ["zo", "ma", "di", "wo", "do", "vr", "za"],
};

const FULL_DAYS = {
  fr: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  nl: ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"],
};

const SHORT_MONTHS = {
  fr: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  nl: ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"],
};

const langOf = (lang) => (lang === "en" || lang === "nl" ? lang : "fr");

/** Midi local : aucun décalage d'heure d'été ne peut changer le jour. */
const localDay = (y, m, d) => new Date(y, m, d, 12, 0, 0, 0);

/**
 * Le jour écrit dans la chaîne est le jour de la commande. Un RFC 822
 * « Thu, 24 Sep 2026 00:00:00 +0000 » passé à `new Date()` deviendrait le
 * 23 à l'ouest de Greenwich : on lit donc le jour, pas l'instant.
 *
 * Formats : YYYY-MM-DD, RFC 822, « Thursday - 2026-09-24 », « 24 Sep 2026 ».
 */
export function parseOrderDate(raw) {
  if (!raw) return null;
  if (raw instanceof Date) {
    return isNaN(raw.getTime())
      ? null
      : localDay(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const s = String(raw).trim();

  const rfc = s.match(/^\w{3},\s+(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/);
  if (rfc) {
    const m = MONTHS_EN.indexOf(rfc[2].toLowerCase());
    if (m >= 0) return localDay(Number(rfc[3]), m, Number(rfc[1]));
  }

  const human = s.match(/^(\d{1,2})\s+([A-Za-z]{3})\w*\s+(\d{4})$/);
  if (human) {
    const m = MONTHS_EN.indexOf(human[2].toLowerCase());
    if (m >= 0) return localDay(Number(human[3]), m, Number(human[1]));
  }

  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = localDay(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
}

/** « jeu. 24 sept. » · « Thu 24 Sep » · « do 24 sep ». Vide si illisible. */
export function formatOrderDate(raw, lang) {
  const d = parseOrderDate(raw);
  if (!d) return "";
  const l = langOf(lang);
  return `${SHORT_DAYS[l][d.getDay()]} ${d.getDate()} ${SHORT_MONTHS[l][d.getMonth()]}`;
}

/** « 08:00-12:00 » → « 08:00 — 12:00 ». */
export const formatSlot = (slot) => (slot ? String(slot).replace("-", " — ") : "");

/** « jeu. 24 sept. · 08:00 — 12:00 », en omettant ce qui manque. */
export const dateSlotLine = (date, slot, lang) =>
  [formatOrderDate(date, lang), formatSlot(slot)].filter(Boolean).join(" · ");

const startOfDay = (now) => localDay(now.getFullYear(), now.getMonth(), now.getDate());

/** Jours entre aujourd'hui et `raw` (négatif dans le passé), ou null. */
export function dayDiff(raw, now = new Date()) {
  const d = parseOrderDate(raw);
  if (!d) return null;
  return Math.round((d.getTime() - startOfDay(now).getTime()) / 86400000);
}

export const isToday = (raw, now = new Date()) => dayDiff(raw, now) === 0;

/** Strictement avant aujourd'hui, heure ignorée. */
export const isPast = (raw, now = new Date()) => {
  const diff = dayDiff(raw, now);
  return diff !== null && diff < 0;
};

/**
 * La plus proche des deux dates encore à venir ; sinon la plus récente ;
 * Infinity si aucune n'est lisible. Trier dessus met en tête la commande
 * dont il se passe quelque chose en premier.
 */
export function nextMilestoneTs(order, now = new Date()) {
  const today = startOfDay(now).getTime();
  const dates = [order?.pickup_date, order?.delivery_date]
    .map((raw) => parseOrderDate(raw))
    .filter(Boolean)
    .map((d) => d.getTime());
  if (!dates.length) return Infinity;
  const upcoming = dates.filter((ts) => ts >= today);
  return upcoming.length ? Math.min(...upcoming) : Math.max(...dates);
}

export const sortByNextMilestone = (orders, now = new Date()) =>
  [...(orders || [])].sort((a, b) => nextMilestoneTs(a, now) - nextMilestoneTs(b, now));

/**
 * « Collecte prévue aujourd'hui / demain / jeudi / le jeu. 24 sept. »,
 * « Collecte programmée » pour une date illisible ou passée.
 */
export function plannedTitle(raw, leg, t, lang, now = new Date()) {
  const key = `planned.${leg === "delivery" ? "delivery" : "pickup"}`;
  const d = parseOrderDate(raw);
  const diff = dayDiff(raw, now);
  if (!d || diff === null || diff < 0) return t(`${key}.fallback`);
  if (diff === 0) return t(`${key}.today`);
  if (diff === 1) return t(`${key}.tomorrow`);
  if (diff <= 6) return t(`${key}.weekday`, { day: FULL_DAYS[langOf(lang)][d.getDay()] });
  return t(`${key}.date`, { date: formatOrderDate(d, lang) });
}

/** Heure « 10:42 » d'un horodatage ISO. */
export function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ── Textes à compteur ───────────────────────────────────────────────────── */

/**
 * Les textes du mobile qui varient avec un nombre ont été exportés en trois
 * variantes (`__0`, `__1`, `__n`) : chaque langue garde sa propre règle
 * (« 0 arrêt » en français, « 0 stops » en anglais) sans passer par les
 * pluriels d'i18next, que nos codes de langue « fn » / « du » dérouteraient.
 */
export const tn = (t, key, n, vars = {}) =>
  t([`${key}__${n === 0 ? "0" : n === 1 ? "1" : "n"}`, key], { ...vars, n });

/* ── Argent ──────────────────────────────────────────────────────────────── */

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
};

/** « €24,50 », « -€3,00 ». */
export const formatMoney = (value) => {
  const n = toNumber(value) || 0;
  return `${n < 0 ? "-" : ""}€${Math.abs(n).toFixed(2).replace(".", ",")}`;
};

const lineTotal = (lines) =>
  (lines || []).reduce(
    (sum, line) =>
      sum + (toNumber(line.product_price ?? line.product?.price) || 0) * (Number(line.quantity) || 0),
    0
  );

/** Montant facturé : subtotal ?? total ?? amount, sinon la somme des lignes. */
export function orderTotal(order) {
  const direct = toNumber(order?.subtotal ?? order?.total ?? order?.amount);
  return direct !== null ? direct : lineTotal(order?.product_orders ?? order?.products);
}

/* ── Commande ────────────────────────────────────────────────────────────── */

const EMPTY_SENTINELS = new Set(["null", "undefined", "nil", "none", "false", "-"]);

/** Chaîne serveur nettoyée : espaces et guillemets parasites, « null » écrit. */
export function cleanServerString(value) {
  if (value === null || value === undefined) return "";
  const trimmed = String(value).trim().replace(/^["']+|["']+$/g, "").trim();
  return EMPTY_SENTINELS.has(trimmed.toLowerCase()) ? "" : trimmed;
}

/** « Zencorp · Rue X 12, 1000 Bruxelles », ou l'adresse seule à domicile. */
export function placeLine(order) {
  const name = cleanServerString(order?.company) || cleanServerString(order?.shop_name);
  const addr = cleanServerString(order?.address);
  return [name, addr].filter(Boolean).join(" · ");
}

/**
 * `products` → `product_orders`, et l'adresse ramenée à
 * `orderable_id` + `orderable_type`, comme le mobile le fait à la lecture.
 */
export function normalizeOrder(raw) {
  if (!raw) return raw;
  const order = { ...raw, product_orders: raw.product_orders ?? raw.products ?? [] };
  if (raw.collection_point_id) {
    order.orderable_id = raw.collection_point_id;
    order.orderable_type = "CollectionPoint";
  } else if (raw.delivery_address_id) {
    order.orderable_id = raw.delivery_address_id;
    order.orderable_type = "DeliveryAddress";
  }
  return order;
}

/** Commande boutique : ni adresse ni point de collecte. */
export const isShopOrder = (order) =>
  !order?.orderable_id && !order?.delivery_address_id && !order?.collection_point_id;

/** Problème à traiter avant tout le reste, dans l'ordre du mobile. */
export function orderIssue(order) {
  if (order?.pick_up_issue) return "pickup";
  if (order?.delivery_issue) return "delivery";
  if (order?.payment_status === "failed") return "payment";
  return null;
}

/** « Modifier la commande » : jamais en boutique, ni avec un problème ouvert. */
export const canShowEdit = (order) => !isShopOrder(order) && orderIssue(order) === null;

export function editRestrictions(order) {
  const ui = mapUiStatus(order?.status);
  const isPickedUp = ["driver_pickup", "arriving", "at_laundry", "ready", "out_for_delivery"].includes(ui);
  const isProcessed = ["at_laundry", "ready", "out_for_delivery"].includes(ui);
  return {
    canEditProducts: !isProcessed,
    canEditPickup: !isPickedUp,
    canDelete: !isPickedUp,
    paymentLocked: order?.payment_status === "paid",
  };
}

/* ── Suivi en direct ─────────────────────────────────────────────────────── */

/** On ne suit que les commandes dont la collecte ou la livraison est aujourd'hui. */
export const trackingEnabled = (order, now = new Date()) =>
  isToday(order?.pickup_date, now) || isToday(order?.delivery_date, now);

/**
 * Intervalle du prochain appel de suivi, en ms, ou null pour s'arrêter :
 * 120 s après une erreur, arrêt une fois terminé, 30 s quand rien ne bouge,
 * 10 s sinon.
 */
export function trackingInterval({ failed, data }) {
  if (failed) return 120000;
  if (!data) return 10000;
  if (data.state === "completed") return null;
  if (data.state === "confirmed" && data.leg == null) return 30000;
  return 10000;
}

export function derivePhase(payload) {
  if (!payload) return "inactive";
  switch (payload.state) {
    case "completed":
      return "completed";
    case "arriving_soon":
      return "approaching";
    case "on_the_way":
      return (payload.stops_before_user ?? 1) === 0 ? "approaching" : "route_started";
    case "driver_assigned":
    case "confirmed":
      return "pending";
    default:
      return "inactive"; // in_laundry, failed
  }
}

/**
 * Vue du suivi, une seule source pour la carte statut et la frise.
 *
 * `failed` : le dernier appel a échoué. Ce n'est une erreur que s'il n'y a
 * aucune réponse précédente à montrer — un rafraîchissement raté garde le
 * dernier état connu au lieu de vider la carte.
 */
export function liveTrackingView(payload, { failed = false } = {}, t) {
  const isError = Boolean(failed && !payload);
  const phase = isError ? "inactive" : derivePhase(payload);
  const data = isError ? null : payload;
  const leg = data?.leg ?? null;
  const driverName = cleanServerString(data?.driver?.name) || null;
  const vehicle = data?.driver
    ? [data.driver.vehicle_make, data.driver.vehicle_plate].filter(Boolean).join(" · ") || null
    : null;

  const active = data && data.state !== "completed" ? data : null;
  const stopsAway =
    phase === "route_started"
      ? active?.stops_before_user ?? null
      : phase === "approaching"
      ? 0
      : null;
  const position = active?.driver_position ?? null;
  const destination = active?.destination ?? null;
  const slot = active?.slot ?? null;

  let eyebrow = "";
  let title = "";
  let sub = "";
  switch (phase) {
    case "pending":
      if (driverName) {
        eyebrow = t("liveTracking.eyebrowDriverReady");
        title = t("liveTracking.titleDriverReady");
      } else {
        eyebrow = t(leg === "delivery" ? "liveTracking.eyebrowPendingDelivery" : "liveTracking.eyebrowPendingPickup");
        title = t(leg === "delivery" ? "liveTracking.titlePendingDelivery" : "liveTracking.titlePendingPickup");
      }
      sub = driverName ? [driverName, vehicle].filter(Boolean).join(" · ") : slot || "";
      break;
    case "route_started":
      eyebrow = t("liveTracking.eyebrowRoute");
      title =
        stopsAway !== null
          ? tn(t, "liveTracking.titleRoute", stopsAway)
          : t("liveTracking.titleRouteUnknown");
      sub = driverName ? t("liveTracking.subRoute", { driver: driverName }) : "";
      break;
    case "approaching":
      eyebrow = t("liveTracking.eyebrowApproaching");
      title = t("liveTracking.titleApproaching");
      sub = t(leg === "delivery" ? "liveTracking.subApproachingDelivery" : "liveTracking.subApproaching");
      break;
    case "completed":
      eyebrow = t("liveTracking.eyebrowDone");
      title = t(leg === "delivery" ? "liveTracking.titleDoneDelivery" : "liveTracking.titleDonePickup");
      sub = formatTime(data?.completed_at);
      break;
    default:
      break;
  }

  return {
    phase,
    isActive: phase !== "inactive",
    isError,
    leg,
    stopsAway,
    showMap: phase === "approaching" && position !== null,
    driverName,
    vehicle,
    position,
    destination,
    slot,
    copy: { eyebrow, title, sub },
  };
}

/**
 * Le nom du chauffeur : celui du suivi d'abord, puis `driver_name` de la
 * liste des commandes, qui l'annonce même les jours sans suivi.
 */
export const knownDriverName = (order, live) =>
  live?.driverName || cleanServerString(order?.driver_name) || null;

/* ── Carte statut ────────────────────────────────────────────────────────── */

export function statusConfig(order, { t, lang, laundryName, live, now = new Date() }) {
  const ui = mapUiStatus(order?.status);
  const driverName = knownDriverName(order, live) || t("home.driverFallback");
  const stops = live?.stopsAway ?? null;
  const driverTitle =
    stops !== null ? tn(t, "orders.titleStops", stops) : t("orders.driverBanner", { name: driverName });
  const laundry = cleanServerString(order?.laundry_name) || laundryName || "";

  switch (ui) {
    case "arriving":
      return {
        eyebrow: t("orders.statusArriving"),
        title: t("orders.titleArriving", { name: driverName }),
        sub: t("orders.subArriving"),
      };
    case "driver_pickup":
      return { eyebrow: t("orders.statusDriverPickup"), title: driverTitle, sub: driverName };
    case "at_laundry":
      return { eyebrow: t("orders.statusAtLaundry"), title: t("orders.titleAtLaundry"), sub: laundry };
    case "ready": {
      const delivery = dateSlotLine(order?.delivery_date, order?.delivery_slot, lang);
      return {
        eyebrow: t("orders.statusReady"),
        title: t("orders.titleReady"),
        sub: delivery ? t("orders.subDelivery", { date: delivery }) : t("orders.subDeliverySoon"),
      };
    }
    case "out_for_delivery":
      return { eyebrow: t("orders.statusOutDelivery"), title: driverTitle, sub: driverName };
    default:
      return {
        eyebrow: t("orders.statusConfirmed"),
        title: plannedTitle(order?.pickup_date, "pickup", t, lang, now),
        sub: dateSlotLine(order?.pickup_date, order?.pickup_slot, lang),
      };
  }
}

/* ── Frise ───────────────────────────────────────────────────────────────── */

/**
 * Les cinq étapes, portées de `buildTimeline` (orders.tsx). Chaque étape :
 * { id, title, meta, detail, state: done|active|pending, highlight, warn }.
 */
export function buildTimeline(order, { t, lang, laundryName, live, now = new Date() }) {
  const ui = mapUiStatus(order?.status);
  const shop = isShopOrder(order);
  const driverName = knownDriverName(order, live) || t("home.driverFallback");
  const assignedDriver = knownDriverName(order, live);
  const stops = live?.stopsAway ?? null;

  const isPostPickup = ui === "at_laundry" || ui === "ready" || ui === "out_for_delivery";
  const pickupDone = isPostPickup;
  const washedDone = ui === "ready" || ui === "out_for_delivery";
  const foldedDone = ui === "out_for_delivery";

  const pickupState = pickupDone ? "done" : "active";
  const washedState = washedDone ? "done" : pickupDone ? "active" : "pending";
  const foldedState = foldedDone ? "done" : washedDone ? "active" : "pending";
  const deliveryState = foldedDone ? "active" : "pending";
  const deliveryHighlight = deliveryState === "active" && isToday(order?.delivery_date, now);

  const pickupOverdue = !pickupDone && isPast(order?.pickup_date, now);
  const pickupDetail = pickupDone
    ? ""
    : pickupOverdue
    ? t("orders.pickupOverdue")
    : ui === "arriving"
    ? t("orders.arrivalSoon", { name: driverName })
    : ui === "driver_pickup" && stops !== null
    ? tn(t, "orders.stopsBefore", stops, { name: driverName })
    : assignedDriver
    ? t("orders.driverAssigned", { name: assignedDriver })
    : live?.isError
    ? t("orders.trackingUnavailable")
    : t("orders.awaitingDriver");

  const deliveryPending = !shop && isPostPickup && isToday(order?.delivery_date, now);
  const deliveryDetail = !deliveryPending
    ? ""
    : stops === 0
    ? t("orders.arrivalSoon", { name: driverName })
    : stops !== null
    ? tn(t, "orders.stopsBefore", stops, { name: driverName })
    : assignedDriver
    ? t("orders.driverAssignedDelivery", { name: assignedDriver })
    : live?.isError
    ? t("orders.trackingUnavailable")
    : t("orders.awaitingDriver");

  const placedAt = order?.updated_at || order?.state_updated_at;

  return [
    {
      id: "placed",
      title: t("orders.orderPlaced"),
      meta: (placedAt && formatOrderDate(String(placedAt).slice(0, 10), lang)) || t("orders.recently"),
      detail: formatMoney(orderTotal(order)),
      state: "done",
    },
    {
      id: "pickup",
      title: t("orders.pickup"),
      meta: dateSlotLine(order?.pickup_date, order?.pickup_slot, lang) || t("orders.scheduled"),
      detail: pickupDetail,
      state: pickupState,
      warn: pickupOverdue,
    },
    {
      id: "washed",
      title: t("orders.beingWashed"),
      meta: cleanServerString(order?.laundry_name) || laundryName || "",
      detail: "",
      state: washedState,
    },
    { id: "folded", title: t("orders.foldedReady"), meta: "", detail: "", state: foldedState },
    {
      id: "delivery",
      title: t(shop ? "orders.readyInShop" : "orders.outForDelivery"),
      meta: shop ? "" : dateSlotLine(order?.delivery_date, order?.delivery_slot, lang),
      detail: deliveryDetail,
      state: deliveryState,
      highlight: deliveryHighlight,
    },
  ].map((step) => ({ warn: false, highlight: false, ...step }));
}

/* ── Détail de la commande (accordéon) ──────────────────────────────────── */

export function localizedLineName(line, lang) {
  const fr = line?.product_name ?? line?.product?.name ?? "";
  if (lang === "nl") return line?.product_name_nl || fr;
  if (lang === "en") return line?.product_name_en || fr;
  return fr;
}

/**
 * Lignes, frais de l'adresse (déjà chargée : livraison à domicile, service
 * au point de collecte, seulement si > 0), promo et total.
 */
export function orderSummary(order, { lang, homeAddresses = [], officeAddresses = [] } = {}) {
  const lines = order?.product_orders ?? [];
  const items = lines.map((line) => {
    const price = toNumber(line.product_price ?? line.product?.price);
    return {
      id: line.id,
      qty: `${line.quantity}×`,
      name: localizedLineName(line, lang),
      total: price !== null ? price * (Number(line.quantity) || 0) : null,
    };
  });

  let fee = null;
  const sameId = (a) => String(a?.id) === String(order?.orderable_id);
  if (order?.orderable_type === "CollectionPoint") {
    const cp = (officeAddresses || []).find(sameId);
    const amount = toNumber(cp?.service_fee) || 0;
    if (amount > 0) fee = { kind: "service", amount };
  } else if (order?.orderable_type === "DeliveryAddress") {
    const da = (homeAddresses || []).find(sameId);
    const amount = toNumber(da?.delivery_fee) || 0;
    if (amount > 0) fee = { kind: "delivery", amount };
  }

  const itemsTotal = lineTotal(lines);
  const total = toNumber(order?.subtotal ?? order?.total ?? order?.amount);
  const promoCode = cleanServerString(order?.promo_code);
  const hasBreakdown = total !== null && Math.abs(total - itemsTotal) > 0.005;

  return {
    items,
    fee,
    promo: promoCode ? { code: promoCode, discount: hasBreakdown ? itemsTotal - total : null } : null,
    total,
  };
}

/* ── Paiement échoué ─────────────────────────────────────────────────────── */

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function paymentIssue(order, { walletBalance = 0, cards = [] } = {}, t) {
  const isCard = order?.payment_type === "Card";
  const total = orderTotal(order);
  const balance = Number(walletBalance) || 0;
  const missing = balance < 0 ? Math.abs(balance) : total > 0 ? Math.max(total - balance, 0) : 0;
  const card = (cards || []).find((c) => String(c?.id) === String(order?.payment_method_id));
  const cardLabel = card
    ? `${capitalise(card.brand || "")} •• ${card.last4}`.trim()
    : isCard
    ? t("orders.paymentCard")
    : null;
  return { isCard, total, balance, missing, cardLabel };
}

/* ── Carte d'approche ────────────────────────────────────────────────────── */

/** Distance à vol d'oiseau, en mètres. */
export function haversineMeters(a, b) {
  const R = 6371000;
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** « 450 m », « 1,2 km » — volontairement arrondi. */
export function formatDistance(meters) {
  if (meters >= 1000) return `${(meters / 1000).toFixed(1).replace(".", ",")} km`;
  return `${Math.max(50, Math.round(meters / 50) * 50)} m`;
}

/* ── Accueil ─────────────────────────────────────────────────────────────── */

/**
 * Carte statut de l'accueil (OrderHeroCard de home.tsx) : même texte que
 * « Mes commandes », sauf pour une commande confirmée sans date de collecte,
 * qui annonce la livraison, puis « Commande confirmée » en dernier recours.
 */
export function homeStatusConfig(order, context) {
  if (mapUiStatus(order?.status) !== "to_confirm") return statusConfig(order, context);
  const { t, lang, now = new Date() } = context;
  const pickup = dateSlotLine(order?.pickup_date, order?.pickup_slot, lang);
  const delivery = dateSlotLine(order?.delivery_date, order?.delivery_slot, lang);
  return {
    eyebrow: t("orders.statusConfirmed"),
    title: order?.pickup_date
      ? plannedTitle(order.pickup_date, "pickup", t, lang, now)
      : order?.delivery_date
      ? plannedTitle(order.delivery_date, "delivery", t, lang, now)
      : t("home.titleConfirmed"),
    sub: pickup || (delivery ? t("orders.subDelivery", { date: delivery }) : t("home.subConfirmed")),
  };
}

/** Commandes déposées dans un casier premium et pas encore récupérées. */
export const lockerOrders = (pastOrders) =>
  (pastOrders || []).filter((order) => order?.premium_cp === true && order?.status === "delivery");
